import { createContext, ReactNode, useContext, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';
import { ADMIN_CHAT, ADMIN_DELIVERIES } from '../../routes/adminConstants';
import type { RouteObjectType } from '../../types/shared.types';

/**
 * Which screens wear the bell: the main ones, the way the mobile apps have it.
 *
 * A main screen is one the sidebar lists - a route with a `name`. A screen
 * opened from one (an order, a location, a transaction) has none, and neither
 * do these, on purpose:
 *
 *   - Chat, which has its own counter for messages;
 *   - Deliveries, whose header is already full (asked for 2026-10-22).
 *
 * Taken from the app's own route table rather than listed here, so a new
 * sidebar entry gets the bell without anyone remembering this file.
 *
 * A function, not a constant: `routes/adminConstants` imports the pages, so
 * reading its values while this module loads closes that cycle and throws
 * "Cannot access 'ADMIN_CHAT' before initialization".
 */
const withoutBell = (): readonly string[] => [ADMIN_CHAT, ADMIN_DELIVERIES];

const BellScreensContext = createContext<readonly string[] | null>(null);

type Props = {
  routes: RouteObjectType[];
  children: ReactNode;
};

export const BellScreensProvider = ({ routes, children }: Props) => {
  const paths = useMemo(() => {
    const excluded = withoutBell();
    return (routes ?? [])
      .filter(({ name, path }) => !!name && !excluded.includes(path))
      .map(({ path }) => path);
  }, [routes]);

  return (
    <BellScreensContext.Provider value={paths}>
      {children}
    </BellScreensContext.Provider>
  );
};

/** Whether the current screen is one of them. False outside the layout. */
export const useIsBellScreen = () => {
  const paths = useContext(BellScreensContext);
  const { pathname } = useLocation();
  return !!paths?.some((path) => matchPath({ path, end: true }, pathname));
};
